import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cardsAPI } from '../../services/api';

export interface Card {
  id: string;
  cardNumberMasked: string;
  cardType: 'visa' | 'mastercard' | 'amex' | 'discover';
  expiryMonth: number;
  expiryYear: number;
  cardholderName: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AddCardRequest {
  cardNumber: string;
  expiryMonth: number;
  expiryYear: number;
  cvv: string;
  cardholderName: string;
}

export interface CardsState {
  cards: Card[];
  isLoading: boolean;
  error: string | null;
  maxCards: number;
  // Hosted checkout state
  cardAddFee?: { amount: number; currency: string; description: string } | null;
  hostedCheckoutUrl?: string | null;
  hostedCheckoutReference?: string | null;
  hostedCheckoutLoading: boolean;
}

const initialState: CardsState = {
  cards: [],
  isLoading: false,
  error: null,
  maxCards: 3,
  cardAddFee: null,
  hostedCheckoutUrl: null,
  hostedCheckoutReference: null,
  hostedCheckoutLoading: false,
};

// Async thunks
export const fetchCards = createAsyncThunk(
  'cards/fetchCards',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cardsAPI.getCards();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch cards');
    }
  }
);

export const initializeCardLink = createAsyncThunk(
  'cards/initializeCardLink',
  async (callbackUrl: string | undefined, { rejectWithValue }) => {
    try {
      const response = await cardsAPI.initializeCardLink(callbackUrl);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to initialise card linking');
    }
  }
);

export const verifyCardLink = createAsyncThunk(
  'cards/verifyCardLink',
  async (reference: string, { rejectWithValue }) => {
    try {
      const response = await cardsAPI.verifyCardLink(reference);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to verify card linking');
    }
  }
);

export const setDefaultCard = createAsyncThunk(
  'cards/setDefaultCard',
  async (cardId: string, { rejectWithValue }) => {
    try {
      const response = await cardsAPI.setDefaultCard(cardId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to set default card');
    }
  }
);

export const deleteCard = createAsyncThunk(
  'cards/deleteCard',
  async (cardId: string, { rejectWithValue }) => {
    try {
      await cardsAPI.deleteCard(cardId);
      return { cardId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to delete card');
    }
  }
);

const cardsSlice = createSlice({
  name: 'cards',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCardAddFlow: (state) => {
      state.cardAddFee = null;
      state.hostedCheckoutUrl = null;
      state.hostedCheckoutReference = null;
      state.hostedCheckoutLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Cards
      .addCase(fetchCards.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCards.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cards = action.payload.cards;
        state.maxCards = action.payload.maxCards;
        state.error = null;
      })
      .addCase(fetchCards.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Add Card
      .addCase(setDefaultCard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(setDefaultCard.fulfilled, (state, action) => {
        state.isLoading = false;
        const cardId = action.meta.arg;
        state.cards = state.cards.map(card => ({
          ...card,
          isDefault: card.id === cardId,
        }));
        state.error = null;
      })
      .addCase(setDefaultCard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Initialize Card Link (hosted checkout)
      .addCase(initializeCardLink.pending, (state) => {
        state.hostedCheckoutLoading = true;
        state.error = null;
        state.hostedCheckoutUrl = null;
        state.hostedCheckoutReference = null;
      })
      .addCase(initializeCardLink.fulfilled, (state, action) => {
        state.hostedCheckoutLoading = false;
        state.hostedCheckoutUrl = action.payload.authorization_url;
        state.hostedCheckoutReference = action.payload.reference;
        state.error = null;
      })
      .addCase(initializeCardLink.rejected, (state, action) => {
        state.hostedCheckoutLoading = false;
        state.error = action.payload as string;
      })
      // Verify Card Link (hosted checkout)
      .addCase(verifyCardLink.pending, (state) => {
        state.hostedCheckoutLoading = true;
        state.error = null;
      })
      .addCase(verifyCardLink.fulfilled, (state, action) => {
        state.hostedCheckoutLoading = false;
        if (action.payload.card) {
          state.cards.push(action.payload.card);
          state.cardAddFee = action.payload.fee || null;
        }
        state.hostedCheckoutUrl = null;
        state.hostedCheckoutReference = null;
        state.error = null;
      })
      .addCase(verifyCardLink.rejected, (state, action) => {
        state.hostedCheckoutLoading = false;
        state.error = action.payload as string;
      })
      // Delete Card
      .addCase(deleteCard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cards = state.cards.filter(card => card.id !== action.payload.cardId);
        state.error = null;
      })
      .addCase(deleteCard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCardAddFlow } = cardsSlice.actions;
export default cardsSlice.reducer;
